import { useId, useState } from "react";
import {
	Area,
	CartesianGrid,
	ComposedChart,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { formatCount, Series, seriesOrder } from "../dashboardConfig";
import ChartTooltip from "./ChartTooltip";

/** One point in time: an id, its tooltip title and a value per series (null = no data yet). */
export type TrendDatum = { id: string; title: string } & Record<
	string,
	string | number | null
>;

interface TrendChartProps {
	data: TrendDatum[];
	series: Series[];
	/** Axis text for the point at `index`. */
	tickLabel: (datum: TrendDatum, index: number) => string;
	/** Appended to a tooltip value, e.g. the master tenant's share. */
	valueDetail?: (datum: TrendDatum, seriesKey: string) => string | undefined;
	yAxisSide: "left" | "right";
	/** A right-hand legend moves above the chart when the chart is narrow. */
	legendSide: "top" | "right";
	/**
	 * "gradient": fading area fills, hollow points and vertical grid lines;
	 * "flat": a light area fill under each line and horizontal grid lines.
	 */
	variant: "gradient" | "flat";
	/** Text alternative for screen readers. */
	summary: string;
}

/**
 * Below this chart width a legend uses the series' short names, and a
 * right-hand one moves above the plot, which would otherwise be squeezed.
 */
const COMPACT_LEGEND_WIDTH = 560;

/** Smooth area and line series over time. */
function TrendChart({
	data,
	series,
	tickLabel,
	valueDetail,
	yAxisSide,
	legendSide,
	variant,
	summary,
}: TrendChartProps) {
	const captionId = useId();
	const gradientId = useId().replace(/:/g, "");
	const isGradient = variant === "gradient";
	const [compactLegend, setCompactLegend] = useState(false);
	const legendOnRight = legendSide === "right" && !compactLegend;

	return (
		<figure
			className={`dashboard-chart${isGradient ? " dashboard-chart--points" : ""}`}
		>
			<figcaption id={captionId} className="sr-only">
				{summary}
			</figcaption>
			<div className="dashboard-chart__plot">
				<ResponsiveContainer
					width="100%"
					height="100%"
					minHeight={120}
					onResize={(width) => setCompactLegend(width < COMPACT_LEGEND_WIDTH)}
				>
					<ComposedChart
						// Names the keyboard-focusable chart surface.
						aria-labelledby={captionId}
						data={data}
						margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
					>
						{isGradient && (
							<defs>
								{series.map((item) => (
									<linearGradient
										key={item.key}
										id={`${gradientId}-${item.key}`}
										x1="0"
										y1="0"
										x2="0"
										y2="1"
									>
										<stop
											offset="0%"
											stopColor={item.color}
											stopOpacity={0.5}
										/>
										<stop
											offset="100%"
											stopColor={item.color}
											stopOpacity={0.03}
										/>
									</linearGradient>
								))}
							</defs>
						)}
						<CartesianGrid vertical={isGradient} horizontal={!isGradient} />
						<XAxis
							dataKey="id"
							tickLine={false}
							tickFormatter={(value: string) => {
								const index = data.findIndex((datum) => datum.id === value);
								return index < 0 ? value : tickLabel(data[index], index);
							}}
						/>
						<YAxis
							orientation={yAxisSide}
							allowDecimals={false}
							width={32}
							axisLine={false}
							tickLine={false}
						/>
						<Legend
							position={legendOnRight ? "right" : "top"}
							offset={legendOnRight ? 8 : 0}
							iconSize={10}
							itemSorter={seriesOrder(series)}
							formatter={(value, entry) =>
								(compactLegend &&
									series.find((item) => item.key === entry.dataKey)?.short) ||
								value
							}
						/>
						<Tooltip
							content={({ active, payload }) => {
								const datum = payload?.[0]?.payload as TrendDatum | undefined;
								if (!active || !datum) return null;
								const rows = series.flatMap((item) => {
									const value = datum[item.key];
									if (typeof value !== "number") return [];
									const detail = valueDetail?.(datum, item.key);
									const text = `${item.label}: ${formatCount(value)}`;
									return [
										{
											color: item.color,
											text: detail ? `${text} ${detail}` : text,
										},
									];
								});
								return rows.length ? (
									<ChartTooltip title={datum.title} rows={rows} />
								) : null;
							}}
						/>
						{series.map((item) => (
							<Area
								key={item.key}
								type="monotone"
								dataKey={item.key}
								name={item.label}
								stroke={item.color}
								strokeWidth={1.5}
								fill={
									isGradient ? `url(#${gradientId}-${item.key})` : item.color
								}
								fillOpacity={isGradient ? 1 : 0.18}
								dot={isGradient ? { r: 3.2, strokeWidth: 1.5 } : false}
								activeDot={{ r: 4.5 }}
								connectNulls={false}
							/>
						))}
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</figure>
	);
}

export default TrendChart;
